import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import {
  type AuthUser,
  type TokenResponse,
  parseJwt,
  getProviderAuthUrl,
  exchangeCodeForTokens,
  refreshToken as refreshTokenApi,
  logout as logoutApi,
  loginWithCredentials as loginWithCredentialsApi,
  fetchIdentityProfile,
} from '@/services/authApi';
import { useQueryClient } from '@tanstack/react-query';
import { setAllApiTokens, setAllUnauthorizedHandlers } from '@/services/apiTokenManager';
import { clearSentryUser } from '@/lib/sentry';

// ---------- Constants ----------

/** localStorage key names for token storage (identifiers only — not cryptographic secrets). */
const ACCESS_TOKEN_KEY = 'five_ai_access_token';
const REFRESH_TOKEN_KEY = 'five_ai_refresh_token';

/** Schedule a refresh at 80 % of the token lifetime. */
const REFRESH_RATIO = 0.8;

/** localStorage key for intent/sign-up email (onboarding, Hero, etc.). Used to pass login_hint to auth provider. */
const LOCAL_USER_INFO_KEY = 'five_ai_local_user_info';

/** Keys to clear on logout so Get Started / onboarding don't use stale cached email or session. */
const ONBOARDING_AND_INTENT_KEYS = [
  LOCAL_USER_INFO_KEY,
  'five_ai_onboarding_session_id',
  'five_ai_onboarding_session_email',
  'five_ai_onboarding_answers',
  'five_ai_onboarding_quotes',
  'five_ai_onboarding_current_index',
  'five_ai_onboarding_complete_request',
  'five_ai_onboarding_auth_provider',
  'five_ai_onboarding_plan_code',
  'five_ai_onboarding_selected_plan_data',
];

/** Extra localStorage keys to clear on sign-out so the next user doesn't see old data. */
const LOGOUT_LOCAL_STORAGE_KEYS = [
  'five_coach_session',
  'five_ai_login_next_dest',
  'five_ai_onboarding_returned_from_registration',
];

/** sessionStorage keys to clear on sign-out (e.g. Coach start-lesson payload). */
const LOGOUT_SESSION_STORAGE_KEYS = ['coach_start_lesson'];

function clearOnboardingAndIntentCache(): void {
  if (typeof window === 'undefined') return;
  try {
    ONBOARDING_AND_INTENT_KEYS.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // ignore
  }
}

/** Clear app-specific localStorage and sessionStorage on logout so old data doesn't affect the next user. */
function clearLocalAndSessionStorageOnLogout(): void {
  if (typeof window === 'undefined') return;
  try {
    LOGOUT_LOCAL_STORAGE_KEYS.forEach((key) => window.localStorage.removeItem(key));
    LOGOUT_SESSION_STORAGE_KEYS.forEach((key) => window.sessionStorage.removeItem(key));
  } catch {
    // ignore
  }
}

function getSignUpEmailFromStorage(): string | null {
  try {
    const raw = typeof window !== 'undefined' ? localStorage.getItem(LOCAL_USER_INFO_KEY) : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { signUpEmail?: string; email?: string };
    const email = (parsed.signUpEmail ?? parsed.email ?? '').trim();
    return email || null;
  } catch {
    return null;
  }
}

// ---------- Helpers ----------

/** Extract the `exp` claim (in ms) from a JWT. Returns 0 on failure. */
function getTokenExpMs(token: string): number {
  try {
    const base64Url = token.split('.')[1] || '';
    // JWT payload is base64url, not standard base64. Convert and pad before atob.
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const jsonPayload = atob(padded);
    return (JSON.parse(jsonPayload).exp || 0) * 1000;
  } catch {
    return 0;
  }
}

// ---------- Context types ----------

interface AuthContextValue {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginWithProvider: (provider: string) => Promise<void>;
  handleOAuthCallback: (provider: string, code: string, state: string) => Promise<void>;
  loginWithCredentials: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ---------- Provider ----------

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshTokenValue, setRefreshTokenValue] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Ref to track current access token for cross-tab storage event handler. */
  const accessTokenRef = useRef<string | null>(null);

  // Keep the ref in sync with state.
  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  // ---- helpers ----

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const persistTokens = useCallback(
    (access: string | null, refresh: string | null) => {
      if (typeof window === 'undefined') return;
      if (access) {
        localStorage.setItem(ACCESS_TOKEN_KEY, access);
      } else {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
      }
      if (refresh) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
      } else {
        localStorage.removeItem(REFRESH_TOKEN_KEY);
      }
    },
    []
  );

  const clearAuth = useCallback(() => {
    clearRefreshTimer();
    setUser(null);
    setAccessToken(null);
    setRefreshTokenValue(null);
    persistTokens(null, null);
    setAllApiTokens(null);
    clearOnboardingAndIntentCache();
    clearLocalAndSessionStorageOnLogout();
    queryClient.clear();
    clearSentryUser();
  }, [clearRefreshTimer, persistTokens, queryClient]);

  /**
   * Soft reset for automatic auth failures (401 refresh/handlers).
   * Requirement: only purge auth/tokens/localStorage on explicit `logout()`.
   * So we avoid calling `clearAuth()` in automatic failure paths.
   * The ProtectedRoute 8-second timeout handles stuck loading screens separately.
   */
  const softAuthReset = useCallback(() => {
    setUser(null);
  }, []);

  /** Schedule a proactive refresh timer based on the token's remaining lifetime. */
  const scheduleRefresh = useCallback(
    (token: string, rt: string) => {
      clearRefreshTimer();
      const expMs = getTokenExpMs(token);
      const delay = (expMs - Date.now()) * REFRESH_RATIO;
      if (delay > 0) {
        refreshTimerRef.current = setTimeout(() => {
          doRefresh(rt);
        }, delay);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /**
   * Apply a token response: decode user, persist tokens, sync apiService,
   * and schedule the next refresh.
   */
  const applyTokenResponse = useCallback(
    async (tokens: TokenResponse): Promise<void> => {
      const decoded = parseJwt(tokens.accessToken);
      setAccessToken(tokens.accessToken);
      setRefreshTokenValue(tokens.refreshToken);
      persistTokens(tokens.accessToken, tokens.refreshToken);
      setAllApiTokens(tokens.accessToken);
      scheduleRefresh(tokens.accessToken, tokens.refreshToken);

      if (decoded.email) {
        setUser(decoded);
      } else {
        // JWT lacks email: don't set user until identity profile succeeds; 401 → clear auth.
        try {
          const profile = await fetchIdentityProfile(tokens.accessToken);
          if (profile.email) {
            setUser({
              ...decoded,
              email: profile.email,
              firstName: decoded.firstName ?? profile.firstName,
              lastName: decoded.lastName ?? profile.lastName,
            });
          }
        } catch (err) {
          // Identity service may reject this access token even if it is still usable for
          // other APIs (audience/scope mismatch). Don't clear auth here; preserve the
          // access token so pages can keep working and use email from URL/storage.
          if (err instanceof Error && err.message === 'profile 401') {
            if (import.meta.env.DEV) {
              console.warn('[AuthContext] applyTokenResponse: identity profile 401; preserving tokens (not clearing auth).');
            }
            return;
          }
          // Otherwise ignore (downstream pages have fallbacks).
        }
      }
    },
    [persistTokens, scheduleRefresh]
  );

  const doRefresh = useCallback(
    async (rt: string): Promise<string | null> => {
      try {
        const tokens = await refreshTokenApi(rt);

        // Backend signals that full re-authentication is required
        // (idle timeout exceeded, absolute expiry, compromised token family, etc.)
        if (tokens.requiresReauth) {
          if (import.meta.env.DEV) {
            console.warn('[AuthContext] Re-authentication required:', tokens.reauthProvider);
          }
          // Do not purge auth/tokens automatically; require explicit user re-login/sign-out.
          softAuthReset();
          return null;
        }

        await applyTokenResponse(tokens);
        return tokens.accessToken;
      } catch {
        // Refresh failed — force re-login.
        if (import.meta.env.DEV) {
          console.warn('[AuthContext] Token refresh failed, clearing session.');
        }
        // If access token still looks valid, keep session so pages can continue working.
        // This prevents refresh flakiness/audience mismatches from causing login redirects.
        try {
          const storedAccess =
            accessTokenRef.current ?? (typeof window !== 'undefined' ? localStorage.getItem(ACCESS_TOKEN_KEY) : null);
          const expMs = storedAccess ? getTokenExpMs(storedAccess) : 0;
          const now = Date.now();
          if (storedAccess && (expMs === 0 || expMs > now)) {
            if (import.meta.env.DEV) {
              console.warn('[AuthContext] Token refresh failed but access token still looks valid; preserving auth.', {
                expMs,
                now,
                storedAccessPresent: !!storedAccess,
              });
            }
            return null;
          }
        } catch {
          // ignore
        }

        // Do not purge auth/tokens automatically; allow user to re-login explicitly.
        softAuthReset();
        return null;
      }
    },
    [applyTokenResponse, softAuthReset]
  );

  // ---- public actions ----

  const loginWithProvider = useCallback(async (provider: string) => {
    const loginHint = getSignUpEmailFromStorage() ?? undefined;
    const { authorizationUrl } = await getProviderAuthUrl(provider, loginHint);
    window.location.href = authorizationUrl;
  }, []);

  const handleOAuthCallback = useCallback(
    async (provider: string, code: string, state: string) => {
      setIsLoading(true);
      try {
        const tokens = await exchangeCodeForTokens(provider, code, state);
        await applyTokenResponse(tokens);
      } finally {
        setIsLoading(false);
      }
    },
    [applyTokenResponse]
  );

  const loginWithCredentials = useCallback(
    async (email: string, password: string) => {
      setIsLoading(true);
      try {
        const tokens = await loginWithCredentialsApi(email, password);
        await applyTokenResponse(tokens);
      } finally {
        setIsLoading(false);
      }
    },
    [applyTokenResponse]
  );

  const logout = useCallback(async () => {
    // Best-effort server-side logout -- clear locally regardless.
    if (accessToken && refreshTokenValue) {
      try {
        await logoutApi(accessToken, refreshTokenValue);
      } catch {
        if (import.meta.env.DEV) {
          console.warn('[AuthContext] Server-side logout failed, clearing locally.');
        }
      }
    }
    clearAuth();
    // Always route to login after sign-out so the UI can't stay on protected screens.
    // Note: AuthProvider wraps <Router>, so we can't use useNavigate() here.
    if (typeof window !== 'undefined') window.location.replace('/login');
  }, [accessToken, refreshTokenValue, clearAuth]);

  // ---- bootstrap: restore tokens, wire callbacks, add event listeners ----

  useEffect(() => {
    // --- Restore tokens from localStorage (async so expired-token refresh keeps isLoading=true) ---
    const bootstrap = async () => {
      const storedAccess = localStorage.getItem(ACCESS_TOKEN_KEY);
      const storedRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);

      if (import.meta.env.DEV) {
        console.log('[AuthContext] Bootstrap:', { hasAccessToken: !!storedAccess, hasRefreshToken: !!storedRefresh });
      }

      // Token-only session:
      // If refresh token is missing but access token is still valid, we should keep the user
      // authenticated until the access token actually expires.
      // (This prevents protected pages like /coach from redirecting to /login on refresh.)
      if (storedAccess && !storedRefresh) {
        try {
          const decoded = parseJwt(storedAccess);
          const expMs = getTokenExpMs(storedAccess);
          const now = Date.now();

          if (expMs > 0 && expMs <= now) {
            // Token expired: drop user state, but keep stored tokens until explicit sign-out.
            softAuthReset();
            setIsLoading(false);
            return;
          }

          setAccessToken(storedAccess);
          setRefreshTokenValue(null);
          setAllApiTokens(storedAccess);

          if (decoded.email) {
            setUser(decoded);
          } else {
            // JWT lacks email: fetch it from identity service once.
            try {
              // Debug: identity-service may reject access token even when the token is
              // not expired client-side (token-only session, audience mismatch, etc.).
              const expMs = getTokenExpMs(storedAccess);
              console.debug('[AuthContext] token-only bootstrap: fetchIdentityProfile', {
                accessTokenPresent: true,
                refreshTokenMissing: true,
                expMs,
                accessTokenLength: storedAccess?.length ?? 0,
                decodedHasEmail: !!decoded.email,
                decodedAuthProvider: decoded.authProvider,
                decodedRoles: decoded.roles,
              });
              const profile = await fetchIdentityProfile(storedAccess);
              if (profile.email) {
                setUser({
                  ...decoded,
                  email: profile.email,
                  firstName: decoded.firstName ?? profile.firstName,
                  lastName: decoded.lastName ?? profile.lastName,
                });
              }
            } catch (err) {
              if (err instanceof Error && err.message === 'profile 401') {
                const details = (err as { status?: number; details?: unknown }).details;
                console.warn('[AuthContext] token-only bootstrap: identity profile 401; NOT clearing auth', {
                  errMessage: err.message,
                  errStatus: (err as { status?: number }).status,
                  detailsType: details ? typeof details : null,
                  detailsPreview:
                    typeof details === 'string' ? details.slice(0, 200) : details && typeof details === 'object' ? JSON.stringify(details).slice(0, 200) : null,
                  decodedAuthProvider: decoded.authProvider,
                  decodedRoles: decoded.roles,
                });
                // Access token might still be valid for learning/coaching APIs, but the
                // identity-service profile lookup failed (token-only session).
                // Do not clear tokens/localStorage here; let pages load using
                // email from URL/localStorage and handle any 401s on-demand.
                setIsLoading(false);
                return;
              }
              /* ignore — downstream pages have fallbacks */
            }
          }
        } catch {
          // Preserve tokens on automatic bootstrap failures.
          softAuthReset();
        }

        setIsLoading(false);
        return;
      }

      if (storedAccess && storedRefresh) {
        try {
          const decoded = parseJwt(storedAccess);
          const expMs = getTokenExpMs(storedAccess);
          const now = Date.now();

          // If exp cannot be parsed (expMs === 0), do not assume token is expired.
          // We will rely on 401-based refresh/handlers instead of forcing reauth.
          if (expMs > 0 && expMs <= now) {
            // Token already expired — await refresh so isLoading stays true until we know auth state.
            // If refresh fails, doRefresh does a soft reset (doesn't purge tokens) and we fall through to setIsLoading(false).
            await doRefresh(storedRefresh);
          } else {
            setAccessToken(storedAccess);
            setRefreshTokenValue(storedRefresh);
            setAllApiTokens(storedAccess);
            scheduleRefresh(storedAccess, storedRefresh);

            if (decoded.email) {
              // JWT has email — show user immediately so home shows logged-in state.
              setUser(decoded);
            } else {
              // JWT lacks email (OAuth): don't set user until identity profile succeeds.
              // Home page comes with no user logged in until identity confirms; 401 → clear auth.
              try {
                const profile = await fetchIdentityProfile(storedAccess);
                if (profile.email) {
                  setUser({
                    ...decoded,
                    email: profile.email,
                    firstName: decoded.firstName ?? profile.firstName,
                    lastName: decoded.lastName ?? profile.lastName,
                  });
                }
              } catch (err) {
                if (err instanceof Error && err.message === 'profile 401') {
                  // Access token might be expired/unparseable; try refresh behind the scenes.
                  const refreshedAccess = await doRefresh(storedRefresh);
                  if (!refreshedAccess) return;
                  try {
                    const profileAfterRefresh = await fetchIdentityProfile(refreshedAccess);
                    if (profileAfterRefresh.email) {
                      setUser({
                        ...decoded,
                        email: profileAfterRefresh.email,
                        firstName: decoded.firstName ?? profileAfterRefresh.firstName,
                        lastName: decoded.lastName ?? profileAfterRefresh.lastName,
                      });
                    }
                  } catch {
                    // If identity still fails after refresh, clearAuth is already handled by doRefresh.
                  }
                  return;
                }
                /* ignore — downstream pages have fallbacks */
              }
            }
          }
        } catch {
          // Malformed token — drop user state, but keep stored tokens until explicit sign-out.
          softAuthReset();
        }
      }

      setIsLoading(false);
    };

    bootstrap();

    // --- Wire 401 interceptors on all API services (profile, content-management, etc.) ---
    // So expired token on /profile or any API triggers refresh, then redirect to login if refresh fails.
    const onUnauthorized = async (): Promise<string | null> => {
      const rt = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!rt) return null;
      try {
        const tokens = await refreshTokenApi(rt);
        if (tokens.requiresReauth) return null;
        await applyTokenResponse(tokens);
        return tokens.accessToken;
      } catch {
        return null;
      }
    };
    // IMPORTANT: don't purge auth/tokens on automatic 401s.
    setAllUnauthorizedHandlers(onUnauthorized, softAuthReset);

    // --- Visibility change: check token on tab resume ---
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const stored = localStorage.getItem(ACCESS_TOKEN_KEY);
      if (!stored) {
        // Token was removed (possibly by another tab)
        clearAuth();
        return;
      }
      try {
        const expMs = getTokenExpMs(stored);
        const now = Date.now();
        if (expMs > 0 && expMs <= now) {
          // Token expired while tab was backgrounded — refresh now
          const rt = localStorage.getItem(REFRESH_TOKEN_KEY);
          if (rt) doRefresh(rt);
          else softAuthReset();
        } else {
          // Recalculate refresh timer (setTimeout may have drifted while backgrounded)
          // If exp cannot be parsed (expMs === 0), scheduleRefresh will be a no-op.
          const rt = localStorage.getItem(REFRESH_TOKEN_KEY);
          if (rt) scheduleRefresh(stored, rt);

          // Sync in-memory state if another tab refreshed while we were hidden
          if (stored !== accessTokenRef.current) {
            const decoded = parseJwt(stored);
            setUser(decoded);
            setAccessToken(stored);
            setAllApiTokens(stored);
          }
        }
      } catch {
        softAuthReset();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // --- Cross-tab sync via storage event ---
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === ACCESS_TOKEN_KEY) {
        if (!e.newValue) {
          // Another tab logged out
          clearAuth();
        } else if (e.newValue !== accessTokenRef.current) {
          // Another tab refreshed — apply the new token locally
          try {
            const decoded = parseJwt(e.newValue);
            setUser(decoded);
            setAccessToken(e.newValue);
            setAllApiTokens(e.newValue);
            // Reschedule refresh timer
            const rt = localStorage.getItem(REFRESH_TOKEN_KEY);
            if (rt) scheduleRefresh(e.newValue, rt);
          } catch {
            softAuthReset();
          }
        }
      }
      if (e.key === REFRESH_TOKEN_KEY) {
        if (!e.newValue) {
          // Refresh token removed — force logout
          clearAuth();
        } else {
          setRefreshTokenValue(e.newValue);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);

    // --- Cleanup ---
    return () => {
      clearRefreshTimer();
      setAllUnauthorizedHandlers(null, null);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
    };
    // Run only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- render ----

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        refreshToken: refreshTokenValue,
        // Treat "token-only bootstrap" as authenticated enough to avoid refresh-page redirect loops.
        // Some pages still wait for `user` (email/roles) explicitly.
        isAuthenticated: Boolean(user || accessToken),
        isLoading,
        loginWithProvider,
        handleOAuthCallback,
        loginWithCredentials,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// ---------- Hook ----------

/* eslint-disable-next-line react-refresh/only-export-components */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
